import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { COLOR_PRESETS } from "@duncancooper/brand";
import { renderToStaticMarkup } from "react-dom/server";
import { FlatIcon } from "../components/FlatLogo.js";
import { userConfigurationStorage } from "../userConfigurationStorage.js";

export const THEMES = COLOR_PRESETS;

const ALLOWED_THEME_NAMES = new Set([
  "Midnight Ocean",
  "Default",
  "Blood Moon",
  "Seafoam",
  "Solar Flare",
]);
const DEFAULT_THEME_NAME = "Midnight Ocean";

const _allThemeOptions = THEMES.map((preset, i) => ({ preset, i })).filter(
  ({ preset }) => ALLOWED_THEME_NAMES.has(preset.name),
);
export const THEME_OPTIONS = [
  ..._allThemeOptions.filter(({ preset }) => preset.name === DEFAULT_THEME_NAME),
  ..._allThemeOptions.filter(({ preset }) => preset.name !== DEFAULT_THEME_NAME),
];

export const DARK_BG = "#0f1419";
export const DARK_TEXT = "#ffffff";
export const LIGHT_BG = "#f8f9fa";
export const LIGHT_TEXT = "#111827";

export type ColorMode = "light" | "dark";

interface ThemeCtx {
  themeIndex: number;
  hoveredThemeIndex: number | null;
  isDark: boolean;
  colorMode: ColorMode;
  nextTheme: () => void;
  cycleColorMode: () => void;
  setColorMode: (mode: ColorMode) => void;
  setThemeIndex: (index: number) => void;
  setHoveredThemeIndex: (index: number | null) => void;
  resetToDefaults: () => void;
}

const ThemeContext = createContext<ThemeCtx>({
  themeIndex: 0,
  hoveredThemeIndex: null,
  isDark: true,
  colorMode: "dark",
  nextTheme: () => {},
  cycleColorMode: () => {},
  setColorMode: () => {},
  setThemeIndex: () => {},
  setHoveredThemeIndex: () => {},
  resetToDefaults: () => {},
});

function resolveInitialThemeIndex(): number {
  // Migrate from legacy numeric themeIndex key
  const legacyRaw = localStorage.getItem("themeIndex");
  if (legacyRaw !== null) {
    const legacyIndex = parseInt(legacyRaw, 10);
    const name = THEMES[legacyIndex]?.name;
    if (name) userConfigurationStorage.themeName.set(name);
    localStorage.removeItem("themeIndex");
    return Math.min(legacyIndex, THEMES.length - 1);
  }
  const savedName = userConfigurationStorage.themeName.get();
  if (savedName) {
    const i = THEMES.findIndex((t) => t.name === savedName);
    if (i !== -1) return i;
  }
  return THEMES.findIndex((t) => t.name === DEFAULT_THEME_NAME);
}

function resolveInitialColorMode(): ColorMode {
  // Migrate from legacy boolean isDark key
  const legacyRaw = localStorage.getItem("isDark");
  if (legacyRaw !== null) {
    const mode: ColorMode = legacyRaw === "true" ? "dark" : "light";
    userConfigurationStorage.colorMode.set(mode);
    localStorage.removeItem("isDark");
    return mode;
  }
  const saved = userConfigurationStorage.colorMode.get();
  if (saved === "light" || saved === "dark") return saved;
  return "dark";
}

const COLOR_MODE_CYCLE: ColorMode[] = ["light", "dark"];

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeIndex, setThemeIndex] = useState(resolveInitialThemeIndex);
  const [colorMode, setColorMode] = useState(resolveInitialColorMode);
  const [hoveredThemeIndex, setHoveredThemeIndex] = useState<number | null>(
    null,
  );

  const isDark = colorMode === "dark";

  useEffect(() => {
    const effectiveIndex = hoveredThemeIndex ?? themeIndex;
    const colors = THEMES[effectiveIndex]![isDark ? "dark" : "light"];
    const primary = colors.primary;
    const root = document.documentElement;
    root.style.setProperty("--color-primary", primary);
    root.style.setProperty("--color-secondary", colors.secondary);
    root.style.setProperty("--color-background", isDark ? DARK_BG : LIGHT_BG);
    root.style.setProperty("--color-text", isDark ? DARK_TEXT : LIGHT_TEXT);

    const bg = isDark ? DARK_BG : LIGHT_BG;
    const fg = isDark ? DARK_TEXT : LIGHT_TEXT;
    const html = renderToStaticMarkup(
      <FlatIcon
        text="td"
        size={32}
        separation={-10}
        accentColor={primary}
        backgroundColor={bg}
        textColor={fg}
      />,
    );
    const svg = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("svg")!.outerHTML;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const next = URL.createObjectURL(blob);
    const link = document.getElementById("favicon") as HTMLLinkElement | null;
    if (link) {
      const prev = link.href;
      link.href = next;
      if (prev.startsWith("blob:")) URL.revokeObjectURL(prev);
    }
  }, [hoveredThemeIndex, themeIndex, isDark]);

  useEffect(() => {
    userConfigurationStorage.themeName.set(THEMES[themeIndex]!.name);
    userConfigurationStorage.colorMode.set(colorMode);
  }, [themeIndex, colorMode]);

  return (
    <ThemeContext.Provider
      value={{
        themeIndex,
        hoveredThemeIndex,
        isDark,
        colorMode,
        nextTheme: () => setThemeIndex((i) => (i + 1) % THEMES.length),
        cycleColorMode: () =>
          setColorMode(
            (m) => COLOR_MODE_CYCLE[(COLOR_MODE_CYCLE.indexOf(m) + 1) % 2]!,
          ),
        setColorMode,
        setThemeIndex,
        setHoveredThemeIndex,
        resetToDefaults: () => { setThemeIndex(THEMES.findIndex((t) => t.name === DEFAULT_THEME_NAME)); setColorMode("dark"); },
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
